import React from 'react';
import PropTypes from 'prop-types';
import { ResponsivePie } from '@nivo/pie';
import { withStyles } from '@material-ui/styles';

import { freeSpaceColor, getPieChartColor } from 'constants/pieChartColors';
import ORTypography from 'components/ORTypography/ORTypography';
import { bytesToGB, toPercentage } from 'services/conversions';
import ORSpaceUsageLegend from './ORSpaceUsageLegend/ORSpaceUsageLegend';

const propTypes = {
  classes: PropTypes.shape({}).isRequired,
  spaceUsage: PropTypes.shape({
    sampleTime: PropTypes.number,
    spaceTotal: PropTypes.number,
    spaceAvailable: PropTypes.number,
    spaceUsed: PropTypes.number,
    nodes: PropTypes.arrayOf(
      PropTypes.shape({
        spaceAvailable: PropTypes.number,
        spaceTotal: PropTypes.number,
      }),
    ),
  }),
  instanceIsReady: PropTypes.bool,
  instanceService: PropTypes.string.isRequired,
};

const defaultProps = {
  spaceUsage: {},
  instanceIsReady: true,
};

export function UnstyledORSpaceUsage({ spaceUsage, instanceIsReady, instanceService, classes }) {
  if (!instanceIsReady || Object.keys(spaceUsage).length === 0 || spaceUsage.nodes.length === 0) {
    return (
      <img
        className={classes.placeHolderImage}
        src="/images/stats/nostats.png"
        alt="Stats will go here when available"
      />
    );
  }

  const { spaceTotal, spaceUsed, spaceAvailable, nodes } = spaceUsage;

  // If we have have more services where the total space is based on a single node rather than all
  // nodes added together, add it here.
  const isReplicatedService = ['MongoDB'].includes(instanceService);

  const serviceSpaceAvailable = isReplicatedService ? nodes[0].spaceAvailable : spaceAvailable;

  const freeSpace = {
    id: 'available',
    label: 'available',
    value: parseFloat(bytesToGB(serviceSpaceAvailable).toFixed(2)),
    color: freeSpaceColor,
  };

  const nodeData = nodes.map((node, index) => ({
    id: node.name,
    label: node.name,
    value: parseFloat(bytesToGB(node.spaceUsed).toFixed(2)),
    color: getPieChartColor(index),
  }));

  const serviceNodeData = isReplicatedService ? nodeData.slice(0, 1) : nodeData;

  const nodeDataAndFreeSpace = [...serviceNodeData, freeSpace];

  const serviceTotal = isReplicatedService ? nodes[0].spaceTotal : spaceTotal;

  return (
    <div className={classes.usageContainer}>
      <div className={classes.pieContainer}>
        <div className={classes.usageText}>
          <ORTypography>Usage Details</ORTypography>
          <ORTypography className={classes.usedOutOfTotal}>
            <span className={classes.spaceUsed}>{bytesToGB(spaceUsed).toFixed(2)}</span>
            GB
            <br />
            <span className={classes.spaceTotal}>
              of {bytesToGB(serviceTotal).toFixed(2)}
              GB
            </span>
          </ORTypography>
        </div>

        <div className={classes.pie}>
          <ResponsivePie
            data={nodeDataAndFreeSpace}
            enableRadialLabels={false}
            innerRadius={0.85}
            enableSlicesLabels={false}
            colors={{ datum: 'color' }}
          />
          <div className={classes.percentUsed}>
            <span className={classes.percent}>
              {toPercentage(spaceUsed, serviceTotal).toFixed(2)}%
            </span>
            <br />
            <span className={classes.used}>USED</span>
          </div>
        </div>
      </div>
      <div className={classes.legendContainer}>
        <ORSpaceUsageLegend nodes={nodes} />
      </div>
    </div>
  );
}

UnstyledORSpaceUsage.propTypes = propTypes;
UnstyledORSpaceUsage.defaultProps = defaultProps;

const styles = (theme) => ({
  placeHolderImage: {
    maxWidth: '100%',
    maxHeight: 300,
  },
  usageContainer: {
    border: `1px solid ${theme.customColors.lightGrey}`,
    borderRadius: 5,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1),
    height: '100%',
  },
  pieContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  pie: {
    height: 150,
    width: 150,
    position: 'relative',
  },
  usedOutOfTotal: {
    color: theme.customColors.mediumGrey,
    marginTop: theme.spacing(2),
  },
  spaceUsed: {
    fontSize: '2rem',
    color: theme.palette.text.primary,
  },
  spaceTotal: {
    fontSize: '.9rem',
  },
  percentUsed: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center',
    lineHeight: '1.2em',
  },
  percent: {
    fontSize: '1.4rem',
  },
  used: {
    color: theme.customColors.mediumGrey,
    fontSize: '.8rem',
  },
  legendContainer: {
    overflowY: 'scroll',
    maxHeight: '12.5rem',
    paddingRight: theme.spacing(1),
    marginRight: -theme.spacing(1),
  },
});

export default withStyles(styles)(UnstyledORSpaceUsage);
